import { createSlice } from '@reduxjs/toolkit';
import {
  ExternalUserSubjectsState,
  ExternalUserSubjectsListFetchAction,
  ExternalUserSubjectsListFetchSuccessAction,
  ExternalUserSubjectDetailsFetchAction,
  ExternalUserSubjectDetailsFetchSuccessAction,
  ExternalUserTrialsFetchAction,
  ExternalUserTrialsFetchSuccessAction,
  ExternalUserContextFetchAction,
  ExternalUserContextFetchSuccessAction,
} from './ExternalUserSubjects.types';
import { createSelector, createSliceHook, makeActionCreator } from '../helpers';

const initialState: ExternalUserSubjectsState = {
  loading: false,
  error: undefined,
  data: undefined,
  subjectDetails: undefined,
  search: '',
  selectedSiteFilter: null,
  trials: undefined,
  trialsLoading: false,
  context: undefined,
  contextLoading: false,
  selectedTrialId: null,
};

const { actReject, actReq, actResolve, act } =
  makeActionCreator<ExternalUserSubjectsState>();

export const ExternalUserSubjectsSlice = createSlice({
  name: 'ExternalUserSubjects',
  initialState,
  reducers: {
    fetchExternalUserSubjectsList: actReq<ExternalUserSubjectsListFetchAction['payload']>(),
    fetchExternalUserSubjectsListSuccess:
      actResolve<ExternalUserSubjectsListFetchSuccessAction['payload']>(),
    fetchExternalUserSubjectsListFailure: actReject(),

    fetchExternalUserSubjectDetails: actReq<ExternalUserSubjectDetailsFetchAction['payload']>(),
    fetchExternalUserSubjectDetailsSuccess: act<
      ExternalUserSubjectDetailsFetchSuccessAction['payload']
    >((subjData, state) => ({
      ...state,
      subjectDetails: subjData,
      loading: false,
    })),
    fetchExternalUserSubjectDetailsFailure: actReject(),

    setSearchValue: act<string>((searchValue, state) => ({
      ...state,
      search: searchValue,
    })),

    setSiteFilter: act<string | null>((siteFilter, state) => ({
      ...state,
      selectedSiteFilter: siteFilter,
    })),

    clearSubjectDetails: state => {
      state.subjectDetails = undefined;
    },

    fetchTrials: act<ExternalUserTrialsFetchAction['payload']>(
      (_, state) => ({
        ...state,
        trialsLoading: true,
      })
    ),
    fetchTrialsSuccess: act<ExternalUserTrialsFetchSuccessAction['payload']>(
      (trials, state) => ({
        ...state,
        trials,
        trialsLoading: false,
      })
    ),
    fetchTrialsFailure: act<Error>((error, state) => ({
      ...state,
      error,
      trialsLoading: false,
    })),

    fetchContext: actReq<ExternalUserContextFetchAction['payload']>(),
    fetchContextSuccess: act<ExternalUserContextFetchSuccessAction['payload']>(
      (context, state) => ({
        ...state,
        context,
        contextLoading: false,
      })
    ),
    fetchContextFailure: act<Error>((error, state) => ({
      ...state,
      error,
      contextLoading: false,
    })),

    setSelectedTrial: act<string | null>((trialId, state) => ({
      ...state,
      selectedTrialId: trialId,
    })),
  },
});

export const {
  fetchExternalUserSubjectsList,
  fetchExternalUserSubjectsListSuccess,
  fetchExternalUserSubjectsListFailure,
  fetchExternalUserSubjectDetails,
  fetchExternalUserSubjectDetailsSuccess,
  fetchExternalUserSubjectDetailsFailure,
  setSearchValue,
  setSiteFilter,
  clearSubjectDetails,
  fetchTrials,
  fetchTrialsSuccess,
  fetchTrialsFailure,
  fetchContext,
  fetchContextSuccess,
  fetchContextFailure,
  setSelectedTrial,
} = ExternalUserSubjectsSlice.actions;

export const actions = {
  ...ExternalUserSubjectsSlice.actions,
};

export const useExternalUserSubjectsSlice = createSliceHook(
  ExternalUserSubjectsSlice.name,
  actions
);

export const selectExternalUserSubjectsState = createSelector(ExternalUserSubjectsSlice.name);
